// =============================================================================
// VandieZ admin-web -- KYC review queue (/kyc/queue)
// =============================================================================
// FIFO list of users awaiting KYC review. Click a row to open the per-user
// detail screen (/kyc/users/:userId) where the actual approve/reject
// decisions happen.
//
// Bulk approve: each row has a checkbox; selecting one or more enables the
// "Approve N" toolbar which sweeps every selected user through
// POST /admin/kyc/bulk-approve. The toolbar shows a confirm step before
// firing so an accidental click on the header checkbox can't approve the
// whole page in one shot.
// =============================================================================

import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { kycAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { TableSkeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type { KycAdminQueueEntry } from "@/types/kyc";

const PAGE_SIZE_OPTIONS = [50, 100, 200] as const;
const DEFAULT_PAGE_SIZE = 50;

function formatSubmittedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {return iso;}
  return d.toLocaleString();
}

export function KycQueuePage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [items, setItems] = useState<KycAdminQueueEntry[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmingBulk, setConfirmingBulk] = useState<boolean>(false);
  const [bulkSubmitting, setBulkSubmitting] = useState<boolean>(false);

  const fetchPage = useCallback(
    async (nextOffset: number, signal?: AbortSignal) => {
      if (accessToken === null) {return;}
      setLoading(true);
      setError(null);
      try {
        const res = await kycAdminApi.queue(
          accessToken,
          { limit: pageSize, offset: nextOffset },
          signal,
        );
        setItems(res.items);
        setTotal(res.total);
        setOffset(res.offset);
        // Clear selection on page change so it stays scoped to the
        // visible rows — selecting across pages would confuse the
        // operator.
        setSelected(new Set());
      } catch (e: unknown) {
        if (e instanceof ApiError) {
          setError(e.problem.detail);
        } else if (e instanceof Error && e.name === "AbortError") {
          // ignore — caller is unmounting
        } else {
          setError("Unexpected error. Try again.");
        }
      } finally {
        setLoading(false);
      }
    },
    [accessToken, pageSize],
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchPage(0, controller.signal);
    return () => { controller.abort(); };
  }, [fetchPage]);

  const canPrev = offset > 0;
  const canNext = offset + items.length < total;
  const allSelected = items.length > 0 && selected.size === items.length;
  const someSelected = selected.size > 0 && selected.size < items.length;

  const toggleRow = (id: string): void => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  const toggleAll = (): void => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map((r) => r.id)));
    }
  };

  const submitBulkApprove = async (): Promise<void> => {
    if (accessToken === null || selected.size === 0) {return;}
    setBulkSubmitting(true);
    setError(null);
    try {
      const res = await kycAdminApi.bulkApprove(accessToken, {
        user_ids: Array.from(selected),
      });
      // Summarise the response. Approved + skipped + not_found is the
      // full bucket; the operator probably most cares about how many
      // were approved.
      const parts: string[] = [];
      if (res.approved > 0) {parts.push(`${res.approved} approved`);}
      if (res.skipped > 0) {parts.push(`${res.skipped} skipped`);}
      if (res.not_found > 0) {parts.push(`${res.not_found} not found`);}
      const summary = parts.length > 0 ? parts.join(", ") : "no rows changed";
      if (res.approved > 0) {
        toast.success(`Bulk approve: ${summary}.`);
      } else {
        toast.error(`Bulk approve: ${summary}.`);
      }
      setConfirmingBulk(false);
      setSelected(new Set());
      // Re-fetch so approved rows drop out of the FIFO queue.
      await fetchPage(0);
    } catch (e: unknown) {
      const msg =
        e instanceof ApiError ? e.problem.detail : "Unexpected error.";
      setError(msg);
      toast.error(`Bulk approve failed: ${msg}`);
    } finally {
      setBulkSubmitting(false);
    }
  };

  const selectedNames = useMemo(
    () =>
      items
        .filter((r) => selected.has(r.id))
        .slice(0, 3)
        .map((r) => r.full_name),
    [items, selected],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="KYC review queue"
        description="Users awaiting document review. FIFO order; oldest first."
      />
        <Card>
          <CardHeader>
            <CardTitle>Pending KYC submissions</CardTitle>
            <CardDescription>
              Oldest submission first. Click a row to review documents, or
              select rows and use Approve to sweep multiple in one shot.
              {total > 0 && (
                <span className="ml-1 text-foreground">
                  Showing {offset + 1}–{offset + items.length} of {total}.
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error !== null && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {selected.size > 0 && (
              <div className="mb-3 flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{selected.size} selected</span>
                  {selectedNames.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      ({selectedNames.join(", ")}
                      {selected.size > selectedNames.length ? ", …" : ""})
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setSelected(new Set()); }}
                    disabled={bulkSubmitting}
                  >
                    Clear
                  </Button>
                  {confirmingBulk ? (
                    <>
                      <span className="text-xs text-muted-foreground">
                        Approve {selected.size}?
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setConfirmingBulk(false); }}
                        disabled={bulkSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => void submitBulkApprove()}
                        disabled={bulkSubmitting}
                      >
                        {bulkSubmitting ? (
                          <>
                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            Approving…
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="mr-1 h-3 w-3" />
                            Confirm
                          </>
                        )}
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => { setConfirmingBulk(true); }}
                    >
                      <CheckCircle2 className="mr-1 h-3 w-3" />
                      Approve {selected.size}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {loading && items.length === 0 ? (
              <TableSkeleton columns={7} />
            ) : items.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground">
                Queue is empty. Nothing to review right now.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="py-2 pr-2 w-6">
                        <input
                          type="checkbox"
                          aria-label="Select all on this page"
                          checked={allSelected}
                          ref={(el) => {
                            if (el !== null) {el.indeterminate = someSelected;}
                          }}
                          onChange={toggleAll}
                          className="cursor-pointer"
                        />
                      </th>
                      <th className="py-2 pr-4">Name</th>
                      <th className="py-2 pr-4">Role</th>
                      <th className="py-2 pr-4">Phone</th>
                      <th className="py-2 pr-4">Submitted</th>
                      <th className="py-2 pr-4">Docs</th>
                      <th className="py-2 pr-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => {
                      const isSelected = selected.has(row.id);
                      return (
                        <tr
                          key={row.id}
                          className={cn(
                            "border-b last:border-b-0 hover:bg-muted/40",
                            isSelected && "bg-muted/30",
                          )}
                        >
                          <td className="py-3 pr-2">
                            <input
                              type="checkbox"
                              aria-label={`Select ${row.full_name}`}
                              checked={isSelected}
                              onChange={() => { toggleRow(row.id); }}
                              onClick={(e) => { e.stopPropagation(); }}
                              className="cursor-pointer"
                            />
                          </td>
                          <td
                            className="py-3 pr-4 font-medium cursor-pointer"
                            onClick={() => { navigate(`/kyc/users/${row.id}`); }}
                          >
                            {row.full_name}
                          </td>
                          <td
                            className="py-3 pr-4 capitalize cursor-pointer"
                            onClick={() => { navigate(`/kyc/users/${row.id}`); }}
                          >
                            {row.primary_role}
                          </td>
                          <td
                            className="py-3 pr-4 font-mono text-xs cursor-pointer"
                            onClick={() => { navigate(`/kyc/users/${row.id}`); }}
                          >
                            {row.phone_masked}
                          </td>
                          <td
                            className="py-3 pr-4 text-muted-foreground cursor-pointer"
                            onClick={() => { navigate(`/kyc/users/${row.id}`); }}
                          >
                            {formatSubmittedAt(row.submitted_at)}
                          </td>
                          <td
                            className="py-3 pr-4 cursor-pointer"
                            onClick={() => { navigate(`/kyc/users/${row.id}`); }}
                          >
                            {row.document_count}
                          </td>
                          <td className="py-3 pr-4">
                            <Button
                              variant="link"
                              size="sm"
                              className="px-0"
                              onClick={() => { navigate(`/kyc/users/${row.id}`); }}
                            >
                              Review →
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                disabled={!canPrev || loading}
                onClick={() => void fetchPage(Math.max(0, offset - pageSize))}
              >
                <ChevronLeft className="mr-1 h-4 w-4" /> Previous
              </Button>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span>
                  {loading ? "Loading…" : `Page ${Math.floor(offset / pageSize) + 1}`}
                </span>
                <label className="flex items-center gap-1">
                  Rows:
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number.parseInt(e.target.value, 10));
                    }}
                    className="rounded border bg-background px-1 py-0.5 text-xs"
                  >
                    {PAGE_SIZE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </label>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={!canNext || loading}
                onClick={() => void fetchPage(offset + pageSize)}
              >
                Next <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
    </div>
  );
}
